import {
  gsap,
  CameraStates,
  corePhasesManager,
  modes,
  cameraManager,
  audioManager,
  PlayersSortTypes
} from '@powerplay/core-minigames'
import {
  type DisciplinePhaseManager,
  AudioGroups,
  AudioNames,
  BlueBoxTextType
} from '../../types'
import { player } from '../../entities/athlete/player'
import { gameConfig } from '../../config'
import { startPhaseStateManager } from './StartPhaseStateManager'
import { disciplinePhasesManager } from '../DisciplinePhasesManager'
import { tutorialFlow } from '@/app/modes/tutorial/TutorialFlow'
import { tutorialUIChange } from '@/app/modes/tutorial/TutorialUIChange'
import { audioHelper } from '@/app/audioHelper/AudioHelper'
import {
  gamePhaseState,
  actionButtonState,
  startPhaseState,
  blackOverlayState,
  inputsState,
  miniTableState,
  timeState,
  textMessageState,
  uiState
} from '@/stores'
import { worldEnv } from '@/app/entities/env/WorldEnv'
import { playersManager } from '@powerplay/core-minigames'
import { timeLimitManager } from '@/app/TimeLimitManager'

/**
 * Trieda pre startovaciu fazu
 */
export class StartPhaseManager implements DisciplinePhaseManager {

  /** ci sa deje nieco skipnutelne */
  private skippable = true

  /** ci sa deje nieco skipnutelne v druhej faze */
  private skippableSecond = true

  /** ci uz je mozne odstartovat */
  public startable = false

  /** ci uz zobrazit ui player-info-avatar */
  private showName = false

  /** ci bolo skipnute */
  private skipped = false

  /** ci bolo skipnute v druhej faze */
  private skippedSecond = false

  /** ci faza skoncila */
  public ended = false

  /** hodnota na ktorej stlacil */
  public clickedPower = 0

  /** Pocet frameov od zaciatku fazy */
  private framesInPhase = 0

  /** tween na skrytie odrazovej hlasky */
  public startingMessageTween?: gsap.core.Tween

  /** callback na zavolanie po skonceni fazy */
  private callbackEnd: () => unknown

  /** ako dlho bude zobrazena hlaska na odraze */
  private STARTING_MESSAGE_DURATION = 2.15

  /** Premenna pre kameru */
  private cameraInPostIntroState = false

  /** Tween pre startovacie veci */
  private startTween?: gsap.core.Tween

  /** zobrazenie bielej ziary */
  private showShine = false

  /** Ci sa uz odstartovalo alebo nie */
  private started = false


  /**
   * Konstruktor
   */
  public constructor(callbackEnd: () => unknown) {

    this.callbackEnd = callbackEnd

  }

  /**
   * Pripravenie fazy
   */
  public preparePhase(): void {

    this.storeState()
    startPhaseStateManager.disableInputs()
    startPhaseStateManager.enableStartInputs()

  }

  /**
   * Zacatie fazy
   */
  public startPhase(): void {

    playersManager.setStandings()
    worldEnv.setLineBestMeters(playersManager.getBestResultOfStandings(PlayersSortTypes.descending), true)
    if (this.ended) {

      this.reset()


      if (modes.isTutorial()) {

        // musime doplnit vsade hracovu poziciu pre kamerove obejkty a potom nastavit tweeny
        player.updateCameraConfigOnStart(CameraStates.disciplineIntro)

        if (gameConfig.cameraConfig.enabled) {

          player.setGameCameraSettings()

        }
        this.afterCameraDisciplineIntro()
        this.removeBlackOverlay()
        return

      }

      this.secondCameraDisciplineIntro()
      this.removeBlackOverlay()
      return

    }

    if (!modes.isTutorial() && corePhasesManager.disciplineActualAttempt === 1) {

      audioManager.play(AudioNames.audienceIntroduction)
      audioHelper.stopAudioWithDelay(AudioNames.audienceIntroNoise)

    }

    console.warn('starting start phase')
    // Zobrazit meno hraca v UI
    this.showName = !modes.isTutorial()
    this.setCameraForDisciplineIntro()
    gsap.to({}, {
      onComplete: () => {

        miniTableState().$patch({
          rowsData: [],
          show: false
        })

      },
      duration: 2
    })

  }

  /**
   * Nastavenie kamery pre intro
   */
  private setCameraForDisciplineIntro(): void {

    if (gameConfig.cameraConfig.enabled) {

      player.setGameCameraSettings()

    }
    if (modes.isTutorial()) {

      this.afterCameraDisciplineIntro()
      tutorialFlow.init()
      tutorialUIChange.init()
      return

    }
    if (!disciplinePhasesManager.isFirstAttempt()) {

      this.afterCameraDisciplineIntro()
      return

    }

    cameraManager.setState(CameraStates.disciplineIntro)
    cameraManager.playTween(false, this.secondCameraDisciplineIntro)

  }

  /** pomocna metoda pre animacie */
  public getCameraInPostIntroState(): boolean {

    return this.cameraInPostIntroState

  }

  /**
   * Druhe disciplinove kamerove intro
   */
  public secondCameraDisciplineIntro = (): void => {

    this.skippable = false
    // Zrusit zobrazenie mena hraca v UI
    this.showName = false

    if (modes.isTrainingMode()) {

      this.afterCameraDisciplineIntro()
      return

    }

    // musime dat chvilku pauzu, aby sa dalo skipovat 2x po sebe
    gsap.to({}, {
      duration: 0.1,
      onComplete: () => {

        cameraManager.setState(CameraStates.disciplineIntroSecond)
        cameraManager.playTween(false, this.afterCameraDisciplineIntro)

        this.skippableSecond = true

      }
    })

  }

  /**
   * Zobrazenie attempt [number] hlasky
   */
  private showAttemptText(): void {

    const lastAttempt = corePhasesManager.disciplineActualAttempt === corePhasesManager.disciplineAttemptsCount
    const showFirstLine = false
    const showSecondLine = !modes.isTutorial()
    startPhaseStateManager.resetTextMessageFinishedEmits(showFirstLine, showSecondLine)

    textMessageState().$patch({
      showFirstLine: showFirstLine,
      showSecondLine: showSecondLine,
      firstLineText: '',
      firstLineTextType: 1,
      secondLineTextType: lastAttempt ? 6 : 5,
      secondLineLeftNumber: corePhasesManager.disciplineActualAttempt,
      showMessage: true,
      showType: 3
    })

  }

  /**
   * Spravenie veci po konci disciplinoveho intra
   */
  private afterCameraDisciplineIntro = (): void => {

    this.skippable = false
    this.skippableSecond = false
    this.showName = false

    // nastavime spravnu poziciu
    player.worldEnvLinesManager.setActualPercentOnStart()

    this.cameraInPostIntroState = true

    cameraManager.setState(CameraStates.discipline)
    cameraManager.changeBaseRenderSettings(undefined, undefined, gameConfig.cameraConfig.fov)

    startPhaseStateManager.postIntroUiState()

    if (modes.isTutorial() && disciplinePhasesManager.attempt === 1) return
    this.launchSystem()

  }

  /**
   * Launch system
   */
  public launchSystem(): void {

    if (!modes.isTrainingMode() && !modes.isTutorial() && !modes.isTournament()) {

      gamePhaseState().showRecords = disciplinePhasesManager.isFirstAttemptInGroup()

    }

    this.showAttemptText()
    uiState().blueBoxTextType = BlueBoxTextType.run

    if (!modes.isTutorial() && !modes.isTrainingMode()) {

      if (corePhasesManager.disciplineActualAttempt === 1) {

        audioManager.fadeOutAudio(AudioNames.audienceIntroduction, 1000)
        if (!audioManager.isAudioGroupPlaying(AudioGroups.commentators)) {

          audioManager.play(AudioNames.commentatorBeforeFirstAttempt)

        }

      } else {

        audioManager.stopAudioByGroup(AudioGroups.audience)

      }

    }

    audioManager.play(AudioNames.audienceClapping)
    audioManager.play(AudioNames.audienceIntroNoise)

    console.log('start attempt')
    if (!modes.isTutorial()) {

      timeLimitManager.setActive(true)
      timeState().showCountdown = true

    }
    this.storeState()
    this.started = true
    this.startable = true
    actionButtonState().disabled = false
    startPhaseStateManager.enableInputs()

  }

  /**
   * Zacatie hybania sa hraca
   */
  private startMovingPlayer(): void {

    this.startable = false

    gamePhaseState().showBar = false
    actionButtonState().isStart = false

    timeLimitManager.setActive(false)
    timeState().showCountdown = false

    this.finishPhase()
    // this.ended = true

  }

  /**
   * Inputy pri starte
   */
  public handleInputs(): void {

    if (this.ended) return
    // zaciatok pohybu hraca
    if (this.startable) {

      this.startMovingPlayer()

    }

    // skip intra
    if (this.skippable && this.framesInPhase > 10) {

      console.log('skippped')
      this.skippable = false
      this.skipped = true
      cameraManager.skipTween()
      this.framesInPhase = 0

    }

    // skip intra druheho
    if (this.skippableSecond && this.framesInPhase > 10) {

      this.skippableSecond = false
      this.skippedSecond = true
      cameraManager.skipTween()
      this.framesInPhase = 0

    }

  }

  /**
   * Update kazdy frame
   */
  public update(): void {

    if (!this.ended) this.framesInPhase++

    // const { isEnabled, startFrames } = gameConfig.autoMove
    // if (this.started && isEnabled && this.framesInPhase >= startFrames) {

    //   // auto odstartovanie
    //   console.log(`auto start po ${this.framesInPhase} frameoch`)
    //   this.startMovingPlayer()
    //   player.velocityManager.setSpeedPower(200)

    // }

    this.storeState()

  }

  /**
   * Create black overlay
   */
  public createBlackOverlay(): void {

    blackOverlayState().showBlackScreen = true

  }

  /**
   * Remove black overlay
   */
  public removeBlackOverlay(): void {

    blackOverlayState().showBlackScreen = false


  }

  /**
   * Ukoncene fazy
   */
  public finishPhase(): void {

    if (!this.ended) {

      console.warn('start phase ended')

      this.ended = true
      this.callbackEnd()
      player.start()
      // tutorialFlow.eventActionTrigger(TutorialEventType.goodStart)

      this.storeState()

      startPhaseStateManager.finishPhaseUiState()
      // startPhaseStateManager.disableInputs()

    }

  }

  /**
   * sets finish phase tween
   */
  public setFinishPhaseTween(): void {

    //

  }

  /**
   * UI update
   */
  private storeState(): void {

    startPhaseState().$patch({
      showName: this.showName,
      showPhase: !this.ended,
      attempt: corePhasesManager.disciplineActualAttempt,
      showShine: this.showShine
    })

  }

  /**
   * reset fazy
   */
  public reset(): void {

    inputsState().disabled = true
    this.skippable = true
    this.skippableSecond = false
    this.startable = false
    this.showName = false
    this.skipped = false
    this.skippedSecond = false
    this.ended = false
    this.clickedPower = 0
    this.started = false
    this.framesInPhase = 0
    this.showShine = false
    this.startTween = undefined
    this.startingMessageTween = undefined
    this.cameraInPostIntroState = false
    this.storeState()

  }

}
